'use strict';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { PENDING_APPROVAL_TAB_KEY } from './pendingApprovalConfig';

const getPageItems = (page) => {
	if (Array.isArray(page)) {
		return page;
	}

	return page?.items ?? [];
};

export const useAccessQueries = ({
	accessTabsConfig,
	activeTabIndex,
	searchText,
	pageSize,
	useUsersQueryHook,
	useManagedProjectsQueryHook,
	useRolesQueryHook,
	useSearchUsersQueryHook,
	useSearchProjectsQueryHook,
	useSearchRolesQueryHook,
	useSalesforcePendingUsersQueryHook,
	useSearchSalesforcePendingUsersQueryHook,
}) => {
	const lastGoodSearchItemsRef = useRef({
		users: new Map(),
		projects: new Map(),
		roles: new Map(),
		pendingApproval: new Map(),
	});

	const currentTabConfig = accessTabsConfig[activeTabIndex];
	const shouldSearchServer = currentTabConfig?.search?.mode === 'server' &&
		searchText.length >= (currentTabConfig.search.minChars || 0);
	const searchQuery = shouldSearchServer ? searchText.toUpperCase() : '';

	const usersQueryResult = useUsersQueryHook({ skip: 0, take: pageSize });
	const projectsQueryResult = useManagedProjectsQueryHook({ skip: 0, take: pageSize });
	const rolesQueryResult = useRolesQueryHook({ skip: 0, take: pageSize });
	const pendingUsersQueryResult = useSalesforcePendingUsersQueryHook(
		{ skip: 0, take: pageSize },
		{ skip: currentTabConfig?.key !== PENDING_APPROVAL_TAB_KEY }
	);
	const searchUsersQueryResult = useSearchUsersQueryHook(
		{ searchText: searchQuery, skip: 0, take: pageSize },
		{ skip: !shouldSearchServer || currentTabConfig?.key !== 'users' }
	);
	const searchProjectsQueryResult = useSearchProjectsQueryHook(
		{ searchText: searchQuery, skip: 0, take: pageSize },
		{ skip: !shouldSearchServer || currentTabConfig?.key !== 'projects' }
	);
	const searchRolesQueryResult = useSearchRolesQueryHook(
		{ searchText: searchQuery, skip: 0, take: pageSize },
		{ skip: !shouldSearchServer || currentTabConfig?.key !== 'roles' }
	);
	const searchPendingUsersQueryResult = useSearchSalesforcePendingUsersQueryHook(
		{ searchText: searchQuery, skip: 0, take: pageSize },
		{ skip: !shouldSearchServer || currentTabConfig?.key !== PENDING_APPROVAL_TAB_KEY }
	);

	const { allItems, searchItems, totalCount, refetchAll, refetchSearch, isSearching, isLoadingAll, isError, error } = useMemo(() => {
		if (!currentTabConfig) {
			return {
				allItems: [],
				searchItems: [],
				totalCount: 0,
				refetchAll: () => {},
				refetchSearch: () => {},
				isSearching: false,
				isLoadingAll: false,
				isError: false,
				error: null,
			};
		}

		const tabKey = currentTabConfig.key;
		const currentSearchQuery = shouldSearchServer ? searchText.toUpperCase() : '';

		const getValidSearchItems = (queryResult, key) => {
			if (!shouldSearchServer) {
				return getPageItems(queryResult.data);
			}

			const currentData = queryResult.currentData ?? queryResult.data;
			if (currentData) {
				return getPageItems(currentData);
			}

			return lastGoodSearchItemsRef.current[key].get(currentSearchQuery) || [];
		};

		if (tabKey === 'users') {
			const validSearchItems = getValidSearchItems(searchUsersQueryResult, 'users');
			return {
				allItems: usersQueryResult.data || [],
				searchItems: validSearchItems,
				totalCount: 0,
				refetchAll: usersQueryResult.refetch,
				refetchSearch: searchUsersQueryResult.refetch,
				isSearching: searchUsersQueryResult.isLoading || searchUsersQueryResult.isFetching || false,
				isLoadingAll: usersQueryResult.isLoading || usersQueryResult.isFetching || false,
				isError: usersQueryResult.isError || searchUsersQueryResult.isError || false,
				error: usersQueryResult.error || searchUsersQueryResult.error || null,
			};
		}
		if (tabKey === 'projects') {
			const validSearchItems = getValidSearchItems(searchProjectsQueryResult, 'projects');
			return {
				allItems: projectsQueryResult.data || [],
				searchItems: validSearchItems,
				totalCount: 0,
				refetchAll: projectsQueryResult.refetch,
				refetchSearch: searchProjectsQueryResult.refetch,
				isSearching: searchProjectsQueryResult.isLoading || searchProjectsQueryResult.isFetching || false,
				isLoadingAll: projectsQueryResult.isLoading || projectsQueryResult.isFetching || false,
				isError: projectsQueryResult.isError || searchProjectsQueryResult.isError || false,
				error: projectsQueryResult.error || searchProjectsQueryResult.error || null,
			};
		}
		if (tabKey === 'roles') {
			const validSearchItems = getValidSearchItems(searchRolesQueryResult, 'roles');
			return {
				allItems: rolesQueryResult.data || [],
				searchItems: validSearchItems,
				totalCount: 0,
				refetchAll: rolesQueryResult.refetch,
				refetchSearch: searchRolesQueryResult.refetch,
				isSearching: searchRolesQueryResult.isLoading || searchRolesQueryResult.isFetching || false,
				isLoadingAll: rolesQueryResult.isLoading || rolesQueryResult.isFetching || false,
				isError: rolesQueryResult.isError || searchRolesQueryResult.isError || false,
				error: rolesQueryResult.error || searchRolesQueryResult.error || null,
			};
		}
		if (tabKey === PENDING_APPROVAL_TAB_KEY) {
			const validSearchItems = getValidSearchItems(searchPendingUsersQueryResult, 'pendingApproval');
			const activePage = shouldSearchServer
				? searchPendingUsersQueryResult.data
				: pendingUsersQueryResult.data;
			return {
				allItems: getPageItems(pendingUsersQueryResult.data),
				searchItems: validSearchItems,
				totalCount: activePage?.totalCount ?? 0,
				refetchAll: pendingUsersQueryResult.refetch,
				refetchSearch: searchPendingUsersQueryResult.refetch,
				isSearching: searchPendingUsersQueryResult.isLoading || searchPendingUsersQueryResult.isFetching || false,
				isLoadingAll: pendingUsersQueryResult.isLoading || pendingUsersQueryResult.isFetching || false,
				isError: pendingUsersQueryResult.isError || searchPendingUsersQueryResult.isError || false,
				error: pendingUsersQueryResult.error || searchPendingUsersQueryResult.error || null,
			};
		}

		return {
			allItems: [],
			searchItems: [],
			totalCount: 0,
			refetchAll: () => {},
			refetchSearch: () => {},
			isSearching: false,
			isLoadingAll: false,
			isError: false,
			error: null,
		};
	}, [
		currentTabConfig,
		usersQueryResult,
		projectsQueryResult,
		rolesQueryResult,
		pendingUsersQueryResult,
		searchUsersQueryResult,
		searchProjectsQueryResult,
		searchRolesQueryResult,
		searchPendingUsersQueryResult,
		shouldSearchServer,
		searchText,
	]);

	useEffect(() => {
		if (!shouldSearchServer) return;
		const currentSearchQuery = searchText.toUpperCase();

		const updateLastGood = (queryResult, key) => {
			const isCurrentQuery = queryResult.originalArgs?.searchText === currentSearchQuery;
			const currentData = queryResult.currentData ?? queryResult.data;
			if (isCurrentQuery && !queryResult.isFetching && !queryResult.isLoading) {
				lastGoodSearchItemsRef.current[key].set(
					currentSearchQuery,
					getPageItems(currentData)
				);
			}
		};

		updateLastGood(searchUsersQueryResult, 'users');
		updateLastGood(searchProjectsQueryResult, 'projects');
		updateLastGood(searchRolesQueryResult, 'roles');
		updateLastGood(searchPendingUsersQueryResult, 'pendingApproval');
	}, [
		shouldSearchServer,
		searchText,
		searchUsersQueryResult.data,
		searchUsersQueryResult.isFetching,
		searchUsersQueryResult.isLoading,
		searchUsersQueryResult.originalArgs,
		searchProjectsQueryResult.data,
		searchProjectsQueryResult.isFetching,
		searchProjectsQueryResult.isLoading,
		searchProjectsQueryResult.originalArgs,
		searchRolesQueryResult.data,
		searchRolesQueryResult.isFetching,
		searchRolesQueryResult.isLoading,
		searchRolesQueryResult.originalArgs,
		searchPendingUsersQueryResult.data,
		searchPendingUsersQueryResult.isFetching,
		searchPendingUsersQueryResult.isLoading,
		searchPendingUsersQueryResult.originalArgs,
	]);

	const refetchActivePendingList = useCallback(async () => {
		if (currentTabConfig?.key !== PENDING_APPROVAL_TAB_KEY) {
			return;
		}

		lastGoodSearchItemsRef.current.pendingApproval.clear();

		const minChars = currentTabConfig.search?.minChars ?? 0;
		const hasSearchText = searchText.length >= minChars;

		if (hasSearchText) {
			await searchPendingUsersQueryResult.refetch();
			return;
		}

		await pendingUsersQueryResult.refetch();
	}, [
		currentTabConfig?.key,
		currentTabConfig?.search?.minChars,
		searchText,
		pendingUsersQueryResult.refetch,
		searchPendingUsersQueryResult.refetch,
	]);

	return {
		currentTabConfig,
		shouldSearchServer,
		searchQuery,
		allItems,
		searchItems,
		totalCount,
		refetchAll,
		refetchSearch,
		refetchActivePendingList,
		isSearching,
		isLoadingAll,
		isError,
		error,
		usersQueryResult,
		projectsQueryResult,
		rolesQueryResult,
		pendingUsersQueryResult,
		searchUsersQueryResult,
		searchProjectsQueryResult,
		searchRolesQueryResult,
		searchPendingUsersQueryResult,
	};
};

export default useAccessQueries;
